/**
 * DashboardTemplate - Pure UI component for Dashboard page
 *
 * This is a presentational component that receives all data and handlers as props.
 * Contains NO business logic, NO data fetching, NO state management.
 *
 * Benefits:
 * - Easy to test with mock props
 * - Reusable in Storybook
 * - Clear prop interface
 * - No side effects
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { StatsGrid, StatsCard } from '@/components/shared/StatsCard'
import { LineChart, AreaChart, BarChart } from '@/components/shared/Charts'
import { Loader2, Users, Building2, Coins, Bot, Layers, TrendingUp } from 'lucide-react'

export interface DashboardTemplateProps {
  // Dashboard stats
  stats?: {
    tenants: { total: number; active: number; growth: number }
    organizations: { total: number; active: number; growth: number }
    users: { total: number; active: number; growth: number }
    agents: { total: number; active: number; growth: number }
    tokens: { consumed: number; growth: number }
  }

  // Analytics data
  analytics?: {
    counts: {
      signupsToday: number
      signupsWeek: number
      agentsCreatedToday: number
      agentsCreatedWeek: number
      agentsUsedToday: number
      agentsUsedWeek: number
      transactionsToday: number
      transactionsWeek: number
      messagesToday: number
      messagesWeek: number
    }
    tokens: {
      total: number
      prompt: number
      completion: number
      totalValue: number
    }
    top: {
      agents: Array<{ id: string; name: string; conversations: number }>
      models: Array<{ model: string; count: number }>
    }
  }

  // Timeseries data
  tokenTrend: Array<{ date: string; value: number }>
  signupTrend: Array<{ date: string; value: number }>

  // Loading states
  isLoading: boolean
  tokenTrendLoading: boolean
  signupTrendLoading: boolean
}

export function DashboardTemplate({
  stats,
  analytics,
  tokenTrend,
  signupTrend,
  isLoading,
  tokenTrendLoading,
  signupTrendLoading
}: DashboardTemplateProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  if (!stats || !analytics) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">
            Unable to load dashboard statistics.
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      {/* Main Stats Grid */}
      <StatsGrid>
        <StatsCard
          title="Total Tenants"
          value={stats.tenants.total}
          change={{
            value: stats.tenants.growth,
            trend: stats.tenants.growth >= 0 ? 'up' : 'down',
            period: 'from last month',
          }}
          icon={<Layers className="h-4 w-4" />}
          format="number"
        />
        <StatsCard
          title="Total Organizations"
          value={stats.organizations.total}
          change={{
            value: stats.organizations.growth,
            trend: stats.organizations.growth >= 0 ? 'up' : 'down',
            period: 'from last month',
          }}
          icon={<Building2 className="h-4 w-4" />}
          format="number"
        />
        <StatsCard
          title="Total Users"
          value={stats.users.total}
          change={{
            value: stats.users.growth,
            trend: stats.users.growth >= 0 ? 'up' : 'down',
            period: 'from last month',
          }}
          icon={<Users className="h-4 w-4" />}
          format="number"
        />
        <StatsCard
          title="Active Agents"
          value={stats.agents.total}
          change={{
            value: stats.agents.growth,
            trend: stats.agents.growth >= 0 ? 'up' : 'down',
            period: 'from last month',
          }}
          icon={<Bot className="h-4 w-4" />}
          format="number"
        />
        <StatsCard
          title="Token Usage"
          value={stats.tokens.consumed}
          change={{
            value: stats.tokens.growth,
            trend: stats.tokens.growth >= 0 ? 'up' : 'down',
            period: 'from last month',
          }}
          icon={<Coins className="h-4 w-4" />}
          format="number"
        />
      </StatsGrid>

      {/* Analytics Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Today's Activity</CardTitle>
            <CardDescription>New signups and agent usage</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Signups Today</span>
              <span className="text-lg font-bold">{analytics.counts.signupsToday}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Agents Used Today</span>
              <span className="text-lg font-bold">{analytics.counts.agentsUsedToday}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Messages Today</span>
              <span className="text-lg font-bold">{analytics.counts.messagesToday}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">This Week</CardTitle>
            <CardDescription>Weekly activity summary</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Signups This Week</span>
              <span className="text-lg font-bold">{analytics.counts.signupsWeek}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Agents Created</span>
              <span className="text-lg font-bold">{analytics.counts.agentsCreatedWeek}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Transactions</span>
              <span className="text-lg font-bold">{analytics.counts.transactionsWeek}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Token Usage</CardTitle>
            <CardDescription>Overall token consumption</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Tokens</span>
              <span className="text-lg font-bold">{analytics.tokens.total.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Value</span>
              <span className="text-lg font-bold">${analytics.tokens.totalValue.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Prompt/Completion</span>
              <span className="text-sm font-medium">
                {analytics.tokens.prompt.toLocaleString()} / {analytics.tokens.completion.toLocaleString()}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trend Charts */}
      <div className="grid gap-4 md:grid-cols-2">
        <LineChart
          title="Token Usage Trend (Last 30 Days)"
          data={tokenTrend}
          dataKeys={[{ key: 'value', label: 'Tokens', color: '#8884d8' }]}
          xAxisKey="date"
          loading={tokenTrendLoading}
          height={300}
        />

        <AreaChart
          title="User Signups (Last 30 Days)"
          data={signupTrend}
          dataKeys={[{ key: 'value', label: 'Signups', color: '#82ca9d' }]}
          xAxisKey="date"
          loading={signupTrendLoading}
          height={300}
        />
      </div>

      {/* Top Agents Bar Chart */}
      {analytics.top.agents.length > 0 && (
        <BarChart
          title="Top Agents by Conversations"
          data={analytics.top.agents.slice(0, 5)}
          dataKeys={[{ key: 'conversations', label: 'Conversations', color: '#ffc658' }]}
          xAxisKey="name"
          height={300}
        />
      )}

      {/* Top Agents and Models */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Top Agents</CardTitle>
            <CardDescription>Most used agents by conversations</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics.top.agents.length > 0 ? (
              <div className="space-y-4">
                {analytics.top.agents.map((agent, index) => (
                  <div key={agent.id} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted font-medium text-sm">
                        #{index + 1}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{agent.name}</p>
                        <p className="text-xs text-muted-foreground">{agent.id}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{agent.conversations}</p>
                      <p className="text-xs text-muted-foreground">conversations</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No agent usage data available
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top Models</CardTitle>
            <CardDescription>Most used AI models</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics.top.models.length > 0 ? (
              <div className="space-y-4">
                {analytics.top.models.map((model, index) => (
                  <div key={model.model} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted font-medium text-sm">
                        #{index + 1}
                      </div>
                      <p className="text-sm font-medium font-mono">{model.model}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium">{model.count}</p>
                      <TrendingUp className="h-4 w-4 text-green-500" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No model usage data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}
